// Lazy-load three.js and its STL/orbit addons so the ~150 KB (gz) bundle is
// code-split and only fetched on product pages that actually render a model.
export async function loadThree() {
    const THREE = await import('three');
    const { STLLoader } = await import('three/examples/jsm/loaders/STLLoader.js');
    const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js');
    return { THREE, STLLoader, OrbitControls };
}

// Build a scene for one STL. `interactive:false` renders a single frame with
// preserveDrawingBuffer so the canvas can be captured via toBlob (snapshot mode).
export function createStlScene({
    THREE,
    STLLoader,
    OrbitControls,
    arrayBuffer,
    canvas,
    width,
    height,
    color = '#9aa0a6',
    background = null,
    interactive = true,
}) {
    const loader = new STLLoader();
    const geometry = loader.parse(arrayBuffer);
    geometry.computeVertexNormals();

    // Center the geometry at the origin (STL coordinates are arbitrary).
    geometry.computeBoundingBox();
    const box = geometry.boundingBox;
    const center = new THREE.Vector3();
    box.getCenter(center);
    geometry.translate(-center.x, -center.y, -center.z);

    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;

    const scene = new THREE.Scene();
    if (background) {
        scene.background = new THREE.Color(background);
    }

    const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(color),
        metalness: 0.1,
        roughness: 0.75,
    });
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const key = new THREE.DirectionalLight(0xffffff, 0.8);
    key.position.set(1, 1, 1);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.4);
    fill.position.set(-1, -0.5, -1);
    scene.add(fill);

    const camera = new THREE.PerspectiveCamera(45, width / height, maxDim / 100, maxDim * 100);
    const dist = maxDim * 2.2;
    camera.position.set(dist, dist * 0.6, dist);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: !background,
        preserveDrawingBuffer: !interactive, // required for toBlob() capture
    });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(width, height, false);

    const render = () => renderer.render(scene, camera);

    let controls = null;
    let frameId = null;
    if (interactive && OrbitControls) {
        controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.enablePan = true;
        const loop = () => {
            frameId = requestAnimationFrame(loop);
            controls.update();
            render();
        };
        loop();
    } else {
        render();
    }

    const dispose = () => {
        if (frameId) {
            cancelAnimationFrame(frameId);
        }
        if (controls) {
            controls.dispose();
        }
        geometry.dispose();
        material.dispose();
        renderer.dispose();
    };

    return { renderer, scene, camera, geometry, material, controls, dispose, render };
}
